import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from '@/components/ui/dropdown-menu';
import { useTenantEntity } from './tenant-entity-provider';
import { ScopeChoices } from './scope-picker';
import { remainingAccess, SupportAccessBanner, useAccessClock } from './temporary-access';

export function ProfileAccess({ elevate, close }: { elevate: () => void; close: () => void }) {
  const access = useTenantEntity();
  const [rolesOpen, setRolesOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    if (!window.matchMedia) return;
    const media = window.matchMedia('(max-width: 767px)');
    const update = () => setMobile(media.matches);
    update(); media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const now = useAccessClock();
  const tenant = access.tenants.find(t => t.id === access.selected?.tenantId);
  const entity = tenant?.entities.find(e => e.id === access.selected?.entityId);
  const roles = [...access.platformRoles, ...(tenant?.roles ?? []), ...(entity?.roles ?? [])].filter((r, i, all) => all.findIndex(x => x.id === r.id && x.source === r.source && x.groupName === r.groupName) === i);
  const platform = access.platformPermissions.some(p => p.startsWith('platform.'));
  const active = access.activeElevations.filter(e => Date.parse(e.expiresAt) > now);
  const roleList = <div role="group" aria-label="My roles" className="mx-1 mb-2 max-h-64 overflow-y-auto rounded-md border bg-muted/20 p-2">
      {roles.filter(r => r.source !== 'elevation').map(r => <div key={`${r.id}:${r.source}:${r.groupName}`} className="flex items-center justify-between gap-2 py-2 text-sm"><span>{r.name}{r.groupName ? ` · ${r.groupName}` : ''}</span><Badge variant="secondary">Active</Badge></div>)}
      {access.eligibleRoles.map(r => <div key={r.eligibleAssignmentId} className="flex items-center justify-between gap-2 py-2 text-sm"><span>{r.name}</span><Badge variant="outline">Eligible</Badge></div>)}
      {active.map(r => <div key={r.id} className="flex items-center justify-between gap-2 py-2 text-sm"><span>{r.roleName}<span className="block text-xs text-muted-foreground">{remainingAccess(r.expiresAt, now)}</span></span><Badge className="bg-amber-100 text-amber-900">Elevated</Badge></div>)}
      {!roles.length && !access.eligibleRoles.length && !active.length && <p className="text-xs text-muted-foreground">No roles in this context</p>}
    </div>;
  return <>
    <DropdownMenuSeparator />
    {mobile ? <>
      <DropdownMenuItem onSelect={e => { e.preventDefault(); setRolesOpen(!rolesOpen); }} aria-expanded={rolesOpen}><ShieldIcon />My roles</DropdownMenuItem>
      {rolesOpen && roleList}
    </> : <DropdownMenuSub>
      <DropdownMenuSubTrigger><ShieldIcon />My roles</DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-[min(20rem,calc(100vw-1rem))]">{roleList}</DropdownMenuSubContent>
    </DropdownMenuSub>}
    {(['tenant', 'entity'] as const).map(kind => <DropdownMenuSub key={kind}>
      <DropdownMenuSubTrigger data-testid={`${kind}-flyout-trigger`} aria-label={`${kind === 'tenant' ? 'Tenant' : 'Entity'}: ${kind === 'tenant' ? access.selected?.tenantName ?? 'Not selected' : access.selected?.entityName ?? 'Not selected'}`}>
        {kind === 'tenant' ? 'Tenant' : 'Entity'}: <span className="ml-auto max-w-40 truncate text-muted-foreground">{kind === 'tenant' ? access.selected?.tenantName : access.selected?.entityName}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-[min(18rem,calc(100vw-1rem))]"><ScopeChoices kind={kind} close={close} /></DropdownMenuSubContent>
    </DropdownMenuSub>)}
    {platform && <DropdownMenuItem asChild><Link to="/admin/platform"><ShieldIcon />Platform administration</Link></DropdownMenuItem>}
    <DropdownMenuItem onSelect={elevate}>Elevate access</DropdownMenuItem>
    <SupportAccessBanner inMenu />
  </>;
}
